import type {
  ActionConnection,
  ActionProviderId,
  ActionProviderInfo,
  AdminUser,
  AuditLogEntry,
  AuthUser,
  BillingStatus,
  Connection,
  CreateTarget,
  Insight,
  LinkType,
  MailAdminSettings,
  Meta,
  NotificationPrefs,
  OnboardingState,
  OrgPriority,
  Plan,
  PlanInfo,
  Profile,
  Project,
  ProviderId,
  ProviderInfo,
  RefundReason,
  SortDir,
  SortKey,
  Status,
  StripeAdminSettings,
  SyncReport,
  Task,
  TaskArtifact,
  TaskCounts,
  TestResult,
  Transaction,
} from '../types';

const BASE = '/api';

export class ApiError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.status = status;
  }
}

async function call<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    ...init,
    // Every request carries the session cookie — the API is entirely
    // cookie-authenticated, including cross-port requests in local dev
    // where the Vite dev server and the API run on different origins.
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...(init?.headers ?? {}) },
  });
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  const data = text ? JSON.parse(text) : {};
  if (!res.ok) throw new ApiError(data?.error ?? `Request failed (${res.status})`, res.status);
  return data as T;
}

export interface TaskFilters {
  sort: SortKey;
  dir: SortDir;
  search: string;
  status: Status | 'all';
  connectionId: string;
  projectId: string;
  includeDone: boolean;
  includeArchived: boolean;
  includeSnoozed: boolean;
  needsTriage: boolean;
  /** YYYY-MM-DD, or '' when that bound isn't set. */
  createdFrom: string;
  createdTo: string;
  updatedFrom: string;
  updatedTo: string;
  dueFrom: string;
  dueTo: string;
}

export const api = {
  me: () => call<{ user: AuthUser | null }>('/auth/me').then((r) => r.user),
  signup: (email: string, password: string, acceptedTerms: boolean) =>
    call<{ user: AuthUser }>('/auth/signup', {
      method: 'POST',
      body: JSON.stringify({ email, password, acceptedTerms }),
    }).then((r) => r.user),
  login: (email: string, password: string) =>
    call<{ user: AuthUser }>('/auth/login', { method: 'POST', body: JSON.stringify({ email, password }) }).then(
      (r) => r.user
    ),
  logout: () => call<void>('/auth/logout', { method: 'POST' }),
  forgotPassword: (email: string) =>
    call<void>('/auth/forgot-password', { method: 'POST', body: JSON.stringify({ email }) }),
  resetPassword: (token: string, password: string) =>
    call<void>('/auth/reset-password', { method: 'POST', body: JSON.stringify({ token, password }) }),
  resendVerification: () => call<void>('/auth/resend-verification', { method: 'POST' }),

  stopImpersonation: () => call<{ user: AuthUser }>('/impersonation/stop', { method: 'POST' }).then((r) => r.user),

  notificationPrefs: () => call<{ prefs: NotificationPrefs }>('/notifications/prefs').then((r) => r.prefs),
  updateNotificationPrefs: (body: Partial<NotificationPrefs>) =>
    call<{ prefs: NotificationPrefs }>('/notifications/prefs', { method: 'PUT', body: JSON.stringify(body) }).then(
      (r) => r.prefs
    ),

  admin: {
    users: () => call<{ users: AdminUser[] }>('/admin/users').then((r) => r.users),
    impersonate: (id: string) =>
      call<{ user: AuthUser }>(`/admin/users/${id}/impersonate`, { method: 'POST' }).then((r) => r.user),
    auditLog: () => call<{ entries: AuditLogEntry[] }>('/admin/audit-log').then((r) => r.entries),
    stripeSettings: () => call<{ stripe: StripeAdminSettings }>('/admin/settings/stripe').then((r) => r.stripe),
    updateStripeSettings: (body: Partial<StripeAdminSettings>) =>
      call<{ stripe: StripeAdminSettings }>('/admin/settings/stripe', {
        method: 'PUT',
        body: JSON.stringify(body),
      }).then((r) => r.stripe),
    mailSettings: () => call<{ mail: MailAdminSettings }>('/admin/settings/mail').then((r) => r.mail),
    updateMailSettings: (body: Partial<MailAdminSettings>) =>
      call<{ mail: MailAdminSettings }>('/admin/settings/mail', { method: 'PUT', body: JSON.stringify(body) }).then(
        (r) => r.mail
      ),
    testMail: (to: string) =>
      call<{ ok: boolean; error?: string }>('/admin/settings/mail/test', {
        method: 'POST',
        body: JSON.stringify({ to }),
      }),
    transactions: (userId: string) =>
      call<{ transactions: Transaction[] }>(`/admin/users/${userId}/transactions`).then((r) => r.transactions),
    refund: (userId: string, body: { chargeId: string; amount?: number; reason?: RefundReason }) =>
      call<{ transaction: Transaction }>(`/admin/users/${userId}/refund`, {
        method: 'POST',
        body: JSON.stringify(body),
      }).then((r) => r.transaction),
  },

  billingStatus: () => call<{ billing: BillingStatus }>('/billing/status').then((r) => r.billing),
  billingPlans: () => call<{ plans: PlanInfo[] }>('/billing/plans').then((r) => r.plans),
  billingCheckout: (plan: Plan) =>
    call<{ url: string }>('/billing/checkout', { method: 'POST', body: JSON.stringify({ plan }) }).then((r) => r.url),
  billingPortal: () => call<{ url: string }>('/billing/portal', { method: 'POST' }).then((r) => r.url),
  billingTransactions: () => call<{ transactions: Transaction[] }>('/billing/transactions').then((r) => r.transactions),
  billingCancel: () => call<{ billing: BillingStatus }>('/billing/cancel', { method: 'POST' }).then((r) => r.billing),
  billingResume: () => call<{ billing: BillingStatus }>('/billing/resume', { method: 'POST' }).then((r) => r.billing),

  meta: () => call<Meta>('/meta'),
  providers: () => call<{ providers: ProviderInfo[] }>('/providers').then((r) => r.providers),
  insights: () => call<{ insights: Insight[] }>('/insights').then((r) => r.insights),

  profile: () => call<{ profile: Profile }>('/profile').then((r) => r.profile),
  updateProfile: (body: unknown) =>
    call<{ profile: Profile }>('/profile', { method: 'PUT', body: JSON.stringify(body) }).then((r) => r.profile),

  onboarding: () => call<{ onboarding: OnboardingState }>('/onboarding').then((r) => r.onboarding),
  updateOnboarding: (completed: boolean) =>
    call<{ onboarding: OnboardingState }>('/onboarding', {
      method: 'PUT',
      body: JSON.stringify({ completed }),
    }).then((r) => r.onboarding),

  connections: () => call<{ connections: Connection[] }>('/connections').then((r) => r.connections),
  createConnection: (body: unknown) =>
    call<{ connection: Connection }>('/connections', { method: 'POST', body: JSON.stringify(body) }).then(
      (r) => r.connection
    ),
  updateConnection: (id: string, body: unknown) =>
    call<{ connection: Connection }>(`/connections/${id}`, { method: 'PUT', body: JSON.stringify(body) }).then(
      (r) => r.connection
    ),
  deleteConnection: (id: string) => call<void>(`/connections/${id}`, { method: 'DELETE' }),
  testDraft: (body: unknown) =>
    call<{ result: TestResult }>('/connections/test', { method: 'POST', body: JSON.stringify(body) }).then(
      (r) => r.result
    ),
  testConnection: (id: string) =>
    call<{ result: TestResult }>(`/connections/${id}/test`, { method: 'POST' }).then((r) => r.result),
  authorizeConnection: (provider: ProviderId) =>
    call<{ url: string }>(`/connections/oauth/${provider}/authorize`).then((r) => r.url),
  syncConnection: (id: string) =>
    call<{ report: SyncReport }>(`/connections/${id}/sync`, { method: 'POST' }).then((r) => r.report),
  syncAll: () => call<{ reports: SyncReport[] }>('/sync', { method: 'POST' }).then((r) => r.reports),

  createTargets: (connectionId: string) =>
    call<{ targets: CreateTarget[] }>(`/connections/${connectionId}/create-targets`).then((r) => r.targets),
  createExternalTask: (connectionId: string, body: unknown) =>
    call<{ task: Task }>(`/connections/${connectionId}/create-task`, {
      method: 'POST',
      body: JSON.stringify(body),
    }).then((r) => r.task),

  tasks: (filters: TaskFilters) => {
    const params = new URLSearchParams({
      sort: filters.sort,
      dir: filters.dir,
      status: filters.status,
      includeDone: String(filters.includeDone),
      includeArchived: String(filters.includeArchived),
      includeSnoozed: String(filters.includeSnoozed),
      needsTriage: String(filters.needsTriage),
    });
    if (filters.search.trim()) params.set('search', filters.search.trim());
    if (filters.connectionId !== 'all') params.set('connectionId', filters.connectionId);
    if (filters.projectId !== 'all') params.set('projectId', filters.projectId);
    if (filters.createdFrom) params.set('createdFrom', filters.createdFrom);
    if (filters.createdTo) params.set('createdTo', filters.createdTo);
    if (filters.updatedFrom) params.set('updatedFrom', filters.updatedFrom);
    if (filters.updatedTo) params.set('updatedTo', filters.updatedTo);
    if (filters.dueFrom) params.set('dueFrom', filters.dueFrom);
    if (filters.dueTo) params.set('dueTo', filters.dueTo);
    return call<{ tasks: Task[]; counts: TaskCounts }>(`/tasks?${params.toString()}`);
  },
  createTask: (body: unknown) =>
    call<{ task: Task }>('/tasks', { method: 'POST', body: JSON.stringify(body) }).then((r) => r.task),
  updateTask: (id: string, body: unknown) =>
    call<{ task: Task; pushed: boolean; warnings: string[] }>(`/tasks/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  viewTask: (id: string) => call<{ task: Task }>(`/tasks/${id}/view`, { method: 'POST' }).then((r) => r.task),
  snoozeTask: (id: string, until: string | null) =>
    call<{ task: Task }>(`/tasks/${id}/snooze`, { method: 'POST', body: JSON.stringify({ until }) }).then(
      (r) => r.task
    ),
  deleteTask: (id: string) => call<void>(`/tasks/${id}`, { method: 'DELETE' }),

  addLink: (taskId: string, toTaskId: string, type: LinkType, syncEnabled: boolean = false) =>
    call<{ task: Task }>(`/tasks/${taskId}/links`, {
      method: 'POST',
      body: JSON.stringify({ toTaskId, type, syncEnabled }),
    }).then((r) => r.task),
  deleteLink: (linkId: string) => call<void>(`/links/${linkId}`, { method: 'DELETE' }),

  projects: () => call<{ projects: Project[] }>('/projects').then((r) => r.projects),
  createProject: (body: unknown) =>
    call<{ project: Project }>('/projects', { method: 'POST', body: JSON.stringify(body) }).then((r) => r.project),
  updateProject: (id: string, body: unknown) =>
    call<{ project: Project }>(`/projects/${id}`, { method: 'PUT', body: JSON.stringify(body) }).then((r) => r.project),
  deleteProject: (id: string) => call<void>(`/projects/${id}`, { method: 'DELETE' }),

  priorities: () => call<{ priorities: OrgPriority[] }>('/priorities').then((r) => r.priorities),
  createPriority: (body: unknown) =>
    call<{ priority: OrgPriority }>('/priorities', { method: 'POST', body: JSON.stringify(body) }).then(
      (r) => r.priority
    ),
  updatePriority: (id: string, body: unknown) =>
    call<{ priority: OrgPriority }>(`/priorities/${id}`, { method: 'PUT', body: JSON.stringify(body) }).then(
      (r) => r.priority
    ),
  deletePriority: (id: string) => call<void>(`/priorities/${id}`, { method: 'DELETE' }),

  actions: () => call<{ actions: ActionProviderInfo[] }>('/actions').then((r) => r.actions),
  actionConnections: () =>
    call<{ connections: ActionConnection[] }>('/action-connections').then((r) => r.connections),
  createActionConnection: (body: unknown) =>
    call<{ connection: ActionConnection }>('/action-connections', {
      method: 'POST',
      body: JSON.stringify(body),
    }).then((r) => r.connection),
  updateActionConnection: (id: string, body: unknown) =>
    call<{ connection: ActionConnection }>(`/action-connections/${id}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    }).then((r) => r.connection),
  deleteActionConnection: (id: string) => call<void>(`/action-connections/${id}`, { method: 'DELETE' }),
  testActionConnection: (id: string) =>
    call<{ result: TestResult }>(`/action-connections/${id}/test`, { method: 'POST' }).then((r) => r.result),
  authorizeAction: (provider: ActionProviderId) =>
    call<{ url: string }>(`/actions/${provider}/authorize`).then((r) => r.url),

  createArtifact: (taskId: string, provider: ActionProviderId, shareWithOrg?: boolean) =>
    call<{ artifact: TaskArtifact; task: Task }>(`/tasks/${taskId}/artifacts`, {
      method: 'POST',
      body: JSON.stringify({ provider, shareWithOrg }),
    }),
};
